import React, { useContext, useState } from "react";
import DOMPurify from "dompurify";
import { LButton, LFormInput, LDialog, LSwitch, LEditor } from "@loffa/gpp-modules";
import { AuthContext } from "../../../../../context/AuthContext";
import NotificationContext from "../../../../../context/NotificationContext";
import { EMAIL_TEMPLATES_API, MESSAGE_TYPES } from "../constants";
import styles from "./EmailMessageForm.module.css";

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;

function EmailMessageForm(props) {
  const authContext = useContext(AuthContext);
  const notificationContext = useContext(NotificationContext);

  const isEditMode = props.selectedTemplate.emailTemplateGuid !== undefined;

  const [template, setTemplate] = useState({
    name: props.selectedTemplate.name || "",
    messageType: props.selectedTemplate.messageType || "Operations",
    body: props.selectedTemplate.body || "",
    isActive: props.selectedTemplate.isActive !== undefined
      ? props.selectedTemplate.isActive
      : true,
    emailTemplateGuid: props.selectedTemplate.emailTemplateGuid || null,
  });

  const [messageTypes] = useState(MESSAGE_TYPES);
  const [isSaving, setIsSaving] = useState(false);

  const handleFieldChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setTemplate({
      ...template,
      [name]: value,
    });
  };

  const handleBodyChange = (e) => {
    setTemplate((t) => ({ ...t, body: e.html }));
  };

  const toggleActiveState = () => {
    setTemplate((t) => ({ ...t, isActive: !t.isActive }));
  };

  const dialogTitle = isEditMode
    ? "Edit Email Template: " + props.selectedTemplate.name
    : "Add New Email Template";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const method = isEditMode ? "PATCH" : "POST";
    const url = GATEWAY_BASE + EMAIL_TEMPLATES_API;
    const accessToken = authContext.authState().accessToken.accessToken;

    const userGuid = authContext.userInfo().loffaUserGuid;

    const payload = isEditMode
      ? {
          emailTemplateGuid: template.emailTemplateGuid,
          name: template.name,
          messageType: template.messageType,
          body: template.body,
          isActive: template.isActive,
          lastUpdatedBy: userGuid,
        }
      : {
          name: template.name,
          messageType: template.messageType,
          body: template.body,
          createdBy: userGuid,
        };

    try {
      const response = await fetch(url, {
        method: method,
        mode: "cors",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        props.saveForm();
      } else {
        notificationContext.addError("Failed to save email template.");
      }
    } catch (error) {
      console.log(error);
      notificationContext.addError("Failed to save email template.");
    }

    setIsSaving(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear();
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <LDialog
          width="1200"
          title={dialogTitle}
          body={
            <div className={styles.formContainer}>
              <div className={styles.leftColumn}>
                <div className="required-field">* required</div>

                <LFormInput
                  labelName="Template Name *"
                  type="input"
                  onChange={(event) => handleFieldChange(event)}
                  input={{
                    value: template.name,
                    onChange: (event) => handleFieldChange(event),
                    name: "name",
                    required: true,
                  }}
                />

                <LFormInput
                  labelName="Message Type *"
                  type="combobox"
                  comboBox={{
                    name: "messageType",
                    data: messageTypes,
                    textField: "name",
                    dataItemKey: "id",
                    clearButton: false,
                    value: messageTypes.find((t) => t.id === template.messageType),
                    onChange: (event) => {
                      if (event.target.value) {
                        setTemplate({ ...template, messageType: event.target.value.id });
                      }
                    },
                  }}
                />

                <label className={styles.bodyLabel}>Message Body *</label>
                <LEditor
                  value={template.body}
                  onChange={handleBodyChange}
                />

                <LFormInput
                  labelName="Loffa Authorized Signer"
                  type="input"
                  input={{
                    value: "",
                    name: "authorizedSigner",
                    disabled: true,
                    placeholder: "Coming soon",
                  }}
                />

                <LFormInput
                  labelName="Title"
                  type="input"
                  input={{
                    value: "",
                    name: "title",
                    disabled: true,
                    placeholder: "Coming soon",
                  }}
                />

                <LFormInput
                  labelName="Signature"
                  type="input"
                  input={{
                    value: "",
                    name: "signature",
                    disabled: true,
                    placeholder: "Coming soon",
                  }}
                />

                {isEditMode && (
                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
                    <label className={styles.bodyLabel} style={{ margin: 0 }}>Status</label>
                    <LSwitch
                      onChange={toggleActiveState}
                      onLabel="Active"
                      offLabel="Inactive"
                      defaultChecked={template.isActive}
                      width={120}
                    />
                  </div>
                )}

              </div>

              <div className={styles.rightColumn}>
                <label className={styles.bodyLabel}>Preview</label>
                <style>{`#email-preview p { margin: 0 0 1em 0; } #email-preview p:last-child { margin-bottom: 0; }`}</style>
                <div
                  id="email-preview"
                  className={styles.previewPane}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.body) }}
                />
              </div>
            </div>
          }
          actionLayout="end"
          action={
            <div className={styles.actionBar}>
              {isEditMode && (
                <div className={styles.metadataFooter}>
                  <div className={styles.metadataGrid}>
                    <span className={styles.metadataLabel}>Added On:</span>
                    <span>{formatDate(props.selectedTemplate.createdDate)}</span>
                    <span className={styles.metadataLabel}>Updated On:</span>
                    <span>{formatDate(props.selectedTemplate.lastUpdatedDate)}</span>
                  </div>
                  <div className={styles.metadataGrid}>
                    <span className={styles.metadataLabel}>Added By:</span>
                    <span className={styles.metadataValue}>{props.selectedTemplate.createdByName || "—"}</span>
                    <span className={styles.metadataLabel}>Updated By:</span>
                    <span className={styles.metadataValue}>{props.selectedTemplate.lastUpdatedByName || "—"}</span>
                  </div>
                </div>
              )}
              <div className={styles.actionButtons}>
                <LButton onClick={props.closeForm}>Cancel</LButton>
                <LButton
                  themeColor="primary"
                  className="green"
                  type="submit"
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </LButton>
              </div>
            </div>
          }
          actionCloseButton={props.closeForm}
          actionOverlay={props.closeForm}
        />
      </form>
    </div>
  );
}

export { EmailMessageForm };
